"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useState } from "react"
import { Container, ResponsiveMedia } from "ui"
import { extractProperty, formatTextWithLineBreaks, getUmbracoMediaUrl } from "utils"
import GeneralPositionModal from "../Jobs/GeneralPositionModal"

const StyledSection = styled.section`
  position: relative;
  width: 100%;
  height: 490px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 302px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 453px;
  }
`
const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 28px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
  }
`

const ActionButtonBlue = styled(ActionButton)`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);
  gap: 4px;
  min-width: 327px;
  max-width: 335px;
  ${(props) => props.theme.breakpoints.up("md")} {
    min-width: 300px;
    max-width: 328px;
    width: unset;
  }
  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

const StyledMedia = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f1f1f1;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(180deg, rgba(5, 41, 70, 0) 0.43%, #052946 99.67%);
    ${(props) => props.theme.breakpoints.up("md")} {
      background-image: linear-gradient(90deg, rgba(5, 41, 70, 0) 27.53%, #052946 99.11%);
      [dir="ltr"] & {
        background-image: linear-gradient(270deg, rgba(5, 41, 70, 0) 27.53%, #052946 99.11%);
      }
    }
  }
`
const StyledContentContainer = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const StyledContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: white;
  justify-content: flex-end;
  padding-bottom: 40px;
  align-items: center;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 0;
    align-items: flex-start;
    text-align: left;
    justify-content: center;
  }
`

const StyledTitle = styled.h2`
  margin: 0;
  font-family: var(--narkis-condensed) !important;
  font-size: 40px;
  font-weight: 400;
  line-height: 120%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 32px;
    line-height: 130%;
  }
`

const StyledSubTitle = styled.p`
  margin: 0;
  font-size: 20px;
  font-weight: 300;
  line-height: 130%;
  margin-bottom: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    line-height: 150%; /* 36px */
    letter-spacing: -0.48px;
  }
`

type Props = {
  data: any
}

const CareerLobbyBanner = ({ data }: Props) => {
  const desktopImage = extractProperty(data, "imageDesktop")
  const mobileImage = extractProperty(data, "imageMobile")
  const title = extractProperty(data, "title")
  const subTitle = extractProperty(data, "subTitle")
  const button = extractProperty(data, "button")?.[0]
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <StyledSection id="career-lobby-banner">
      <StyledMedia>
        <ResponsiveMedia
          desktopMedia={getUmbracoMediaUrl(desktopImage)}
          mobileMedia={getUmbracoMediaUrl(mobileImage)}
          priority
          alt={title || "Career Lobby Banner"}
        />
      </StyledMedia>
      <StyledContentContainer>
        <Container style={{ height: "100%" }}>
          <StyledContent>
            {title && <StyledTitle>{title}</StyledTitle>}
            {subTitle && <StyledSubTitle>{formatTextWithLineBreaks(subTitle)}</StyledSubTitle>}
            {button?.url && button?.name && (
              <ActionButtonBlue
                variant="contained"
                color="primary"
                onClick={() => setIsModalOpen(true)}
              >
                {button.name}
              </ActionButtonBlue>
            )}
            <GeneralPositionModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
          </StyledContent>
        </Container>
      </StyledContentContainer>
    </StyledSection>
  )
}
export default CareerLobbyBanner
